import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  PersonIdentifierCreateCommand,
  PersonIdentifierUpdateCommand
} from '../../../models/Entities/Person/Identifier/PersonIdentifierModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import IdentifierTypeSelectorComponent from '../Selectors/IdentifierTypeSelector/IdentifierTypeSelector.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './PersonIdentifier.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonIdentifierFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [identifierTypeId, setIdentifierTypeId] = useState<number | undefined>(undefined);
  const [identifier, setIdentifier] = useState<string>('');
  const [hasBeenVerified, setHasBeenVerified] = useState<boolean>(false);
  const [issuedBy, setIssuedBy] = useState<string>('');
  const [issueDate, setIssueDate] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>('');

  useEffect(() => {
    if (props.identifier) {
      setIdentifierTypeId(props.identifier.identifierType?.id);
      setIdentifier(props.identifier.identifier || '');
      setHasBeenVerified(props.identifier.hasBeenVerified || false);
      setIssuedBy(props.identifier.issuedBy || '');
      setIssueDate(CommonFunctions.ToInputDate(props.identifier.issueDate));
      setExpirationDate(CommonFunctions.ToInputDate(props.identifier.expirationDate));
    } else {
      resetForm();
    }
  }, [props.identifier, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setIdentifierTypeId(undefined);
    setIdentifier('');
    setHasBeenVerified(false);
    setIssuedBy('');
    setIssueDate('');
    setExpirationDate('');
    setSubmitted(false);
    setValidation(undefined);
    props.ResetIdentifierVerification();
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!identifierTypeId) {
      errors.push(
        t('persons.identifiers.form.validation.identifierType', {
          defaultValue: 'El tipo de identificación es obligatorio'
        })
      );
    }

    if (!identifier.trim()) {
      errors.push(
        t('persons.identifiers.form.validation.identifier', { defaultValue: 'El identificador es obligatorio' })
      );
    }

    if (issueDate && expirationDate && new Date(expirationDate) < new Date(issueDate)) {
      errors.push(
        t('persons.identifiers.form.validation.dates', {
          defaultValue: 'La fecha de vencimiento debe ser posterior a la de emisión'
        })
      );
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleVerify = () => {
    if (!identifierTypeId || !identifier.trim()) return;

    props.VerifyIdentifier({
      identifierTypeId,
      identifier: identifier.trim(),
      personId: props.PersonId
    });
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: PersonIdentifierCreateCommand = {
      personId: props.PersonId,
      identifierTypeId: identifierTypeId!,
      identifier: identifier.trim(),
      hasBeenVerified,
      issuedBy: issuedBy.trim() || null,
      issueDate: CommonFunctions.FromInputDate(issueDate),
      expirationDate: CommonFunctions.FromInputDate(expirationDate)
    };

    if (props.identifier) {
      props.UpdatePersonIdentifier({ ...payload, id: props.identifier.id } as PersonIdentifierUpdateCommand);
    } else {
      props.CreatePersonIdentifier(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const VerificationResult = props.isIdentifierAvailable !== undefined && (
    <MessageBar intent={props.isIdentifierAvailable ? 'success' : 'warning'} layout="multiline">
      <MessageBarBody>
        {props.isIdentifierAvailable
          ? t('persons.identifiers.form.verification.available', {
              defaultValue: 'El identificador está disponible'
            })
          : t('persons.identifiers.form.verification.taken', {
              defaultValue: 'El identificador ya se encuentra registrado'
            })}
      </MessageBarBody>
    </MessageBar>
  );

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.identifier
            ? t('persons.identifiers.form.editTitle', { defaultValue: 'Editar identificación' })
            : t('persons.identifiers.form.addTitle', { defaultValue: 'Nueva identificación' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <IdentifierTypeSelectorComponent
            selectedIdentifierTypeId={identifierTypeId}
            personType={props.PersonType}
            isRequired
            errorMessage={
              submitted && !identifierTypeId
                ? t('persons.identifiers.form.validation.identifierType', {
                    defaultValue: 'El tipo de identificación es obligatorio'
                  })
                : undefined
            }
            onSelectionChange={(value: number | undefined) => {
              setIdentifierTypeId(value);
              props.ResetIdentifierVerification();
            }}
          />

          <Field
            label={t('persons.identifiers.form.identifier', { defaultValue: 'Identificador' })}
            required
            validationMessage={
              submitted && !identifier.trim()
                ? t('persons.identifiers.form.validation.identifier', {
                    defaultValue: 'El identificador es obligatorio'
                  })
                : undefined
            }
            validationState={submitted && !identifier.trim() ? 'error' : undefined}
          >
            <Input
              value={identifier}
              onChange={(_, data) => {
                setIdentifier(data.value || '');
                props.ResetIdentifierVerification();
              }}
            />
          </Field>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              appearance="secondary"
              onClick={handleVerify}
              disabled={!identifierTypeId || !identifier.trim() || props.isVerifying}
            >
              <Trans
                i18nKey={'persons.identifiers.form.verification.verify'}
                defaultValue="Verificar disponibilidad"
              />
            </Button>
            {props.isVerifying && <Spinner size="tiny" />}
          </div>

          {VerificationResult}

          <Checkbox
            label={t('persons.identifiers.form.hasBeenVerified', { defaultValue: 'Identificación verificada' })}
            checked={hasBeenVerified}
            onChange={(_, data) => setHasBeenVerified(!!data.checked)}
          />

          <Field label={t('persons.identifiers.form.issuedBy', { defaultValue: 'Emitido por' })}>
            <Input value={issuedBy} onChange={(_, data) => setIssuedBy(data.value || '')} />
          </Field>

          <Field label={t('persons.identifiers.form.issueDate', { defaultValue: 'Fecha de emisión' })}>
            <Input type="date" value={issueDate} onChange={(_, data) => setIssueDate(data.value || '')} />
          </Field>

          <Field label={t('persons.identifiers.form.expirationDate', { defaultValue: 'Fecha de vencimiento' })}>
            <Input
              type="date"
              value={expirationDate}
              onChange={(_, data) => setExpirationDate(data.value || '')}
            />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonIdentifierFormComponent as any);
